import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { axiosKK } from '../../services/networkRequest';
import errorMessage from '../../services/errorMessage';
import Button from '../Button';
import { AdminNotice, SectionHead } from './Shared';

/** One property on one order where HubSpot and the portal disagree. */
export interface HubspotChange {
  orderId: number;
  property: string;
  from: string | null;
  to: string | null;
}

/** `POST /admin/hubspot/import-assignments` — a preview unless `dryRun=false`. */
export interface HubspotAssignmentImportResult {
  dryRun: boolean;
  dealsConsidered: number;
  dealsRead: number;
  ordersMatched: number;
  ordersChanged: number;
  changesByProperty: Record<string, number>;
  sample: HubspotChange[];
  /** property -> HubSpot value no partner or location is linked to -> deals carrying it. */
  unlinkedValues: Record<string, Record<string, number>>;
  errors: number;
}

export interface HubspotUnlinkedValue {
  property: string;
  value: string;
  orders: number;
}

const IMPORT = '/admin/hubspot/import-assignments';
const UNLINKED = '/admin/hubspot/unlinked';

export const PROPERTY_LABELS: Record<string, string> = {
  courier: 'Collection Courier',
  return_courier: 'Return Courier',
  warehouse: 'Warehouse',
  collection_eta: 'Collection ETA',
  delivery_eta: 'Delivery ETA',
  collection_notes: 'Collection notes',
  order_cancelled: 'Order cancelled',
};

export const labelOf = (property: string) => PROPERTY_LABELS[property] || property;

/**
 * Brings every live order's couriers, warehouse, ETAs and notes in line with its
 * HubSpot deal, once. Changes from then on travel both ways by themselves; this is
 * for everything set before they did. HubSpot wins wherever the two disagree,
 * because it is where ops assign, so the admin sees the counts first.
 *
 * Below it, the HubSpot values no partner or warehouse location here is linked to:
 * an order HubSpot gives to one of those has nobody assigned here until someone
 * links it on the Partners screen.
 */
export default function HubspotAssignmentImport() {
  const [preview, setPreview] = useState<HubspotAssignmentImportResult>();
  const [done, setDone] = useState<HubspotAssignmentImportResult>();
  const [unlinked, setUnlinked] = useState<HubspotUnlinkedValue[]>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const loadUnlinked = () =>
    axiosKK
      .get<{ values: HubspotUnlinkedValue[] }>(UNLINKED)
      .then(({ data }) => setUnlinked(data.values || []))
      .catch(() => setUnlinked(undefined));

  useEffect(() => {
    loadUnlinked();
  }, []);

  const run = (dryRun: boolean) => {
    setBusy(true);
    setError(undefined);
    axiosKK
      .post<HubspotAssignmentImportResult>(IMPORT, undefined, { params: { dryRun } })
      .then(({ data }) => {
        if (dryRun) {
          setPreview(data);
          setDone(undefined);
        } else {
          setDone(data);
          setPreview(undefined);
          loadUnlinked();
        }
      })
      .catch((err) => errorMessage(setError)(err))
      .finally(() => setBusy(false));
  };

  const counts = (result: HubspotAssignmentImportResult) =>
    Object.entries(result.changesByProperty || {}).map(([p, n]) => `${labelOf(p)}: ${n}`).join(', ');

  return (
    <section className="admin-dashboard-orders" aria-label="HubSpot assignments">
      <SectionHead
        title="HubSpot assignments"
        caption="Bring each live order's couriers, warehouse, ETAs and notes in line with HubSpot. You see the changes first; nothing changes until you confirm."
        actions={
          <Button type="button" variant="secondary" size="sm" onClick={() => run(true)} disabled={busy}>
            {busy && !preview ? 'Checking…' : 'Check HubSpot'}
          </Button>
        }
      />
      <AdminNotice intent="error">{error}</AdminNotice>

      {preview && (
        <div className="admin-hubspot-import" data-testid="hubspot-assignment-preview">
          <p>
            {preview.dealsRead} of {preview.dealsConsidered} live orders' deals were read.{' '}
            {preview.ordersChanged === 0
              ? 'Every one already matches HubSpot.'
              : `${preview.ordersChanged} orders would change (${counts(preview)}).`}
            {preview.errors > 0 && ` ${preview.errors} could not be checked and will be left alone.`}
          </p>
          {preview.sample.length > 0 && (
            <ul className="admin-hubspot-import__sample">
              {preview.sample.slice(0, 12).map((c, i) => (
                <li key={`${c.orderId}-${c.property}-${i}`}>
                  #{c.orderId} {labelOf(c.property)}: {c.from || 'blank'} → {c.to || 'blank'}
                </li>
              ))}
            </ul>
          )}
          {preview.ordersChanged > 0 && (
            <Button type="button" variant="primary" size="sm" onClick={() => run(false)} disabled={busy}>
              {busy ? 'Importing…' : `Update ${preview.ordersChanged} orders from HubSpot`}
            </Button>
          )}
        </div>
      )}

      {done && (
        <AdminNotice intent="success">
          {done.ordersChanged} orders updated from HubSpot
          {done.ordersChanged > 0 ? ` (${counts(done)})` : ''}
          {done.errors > 0 ? `; ${done.errors} could not be updated and were left alone` : ''}.
        </AdminNotice>
      )}

      {unlinked && unlinked.length > 0 && (
        <div className="admin-hubspot-import" data-testid="hubspot-unlinked">
          <p>
            HubSpot names these on live orders, and nothing here is linked to them, so those
            orders have nobody assigned here. Link each one on the{' '}
            <Link to="/admin/partners">Partners</Link> screen: a partner under its name, a
            warehouse location in its Warehouses section.
          </p>
          <ul className="admin-hubspot-import__sample">
            {unlinked.map((u) => (
              <li key={`${u.property}-${u.value}`}>
                {labelOf(u.property)}: {u.value} ({u.orders} {u.orders === 1 ? 'order' : 'orders'})
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
